import {
  ActionIcon,
  Avatar,
  Burger,
  Group,
  Menu,
  Text,
  Title,
  Tooltip,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core';
import { IconHome, IconLogout, IconMoon, IconSun, IconUser } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../auth/auth-store';
import { performLogout } from '../auth/logout';
import { ProjectSwitcher } from '../project/ProjectSwitcher';
import { useProjectStore } from '../project/project-store';

export function TopBar({
  navOpened,
  onToggleNav,
}: {
  navOpened: boolean;
  onToggleNav: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { setColorScheme } = useMantineColorScheme();
  const computed = useComputedColorScheme('light');
  const toggleColorScheme = () => setColorScheme(computed === 'dark' ? 'light' : 'dark');

  const initials = user
    ? user.username
        .split(/[._-\s]/)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase() ?? '')
        .join('')
    : '?';

  return (
    <Group h="100%" px="md" justify="space-between" wrap="nowrap">
      <Group gap="sm" wrap="nowrap">
        <Burger opened={navOpened} onClick={onToggleNav} size="sm" hiddenFrom="sm" />
        <Tooltip label="Home">
          <ActionIcon
            variant="subtle"
            size="lg"
            component={Link}
            to="/"
            onClick={() => useProjectStore.getState().setApp(null)}
            aria-label="home"
          >
            <IconHome size={18} />
          </ActionIcon>
        </Tooltip>
        <Title order={4} style={{ lineHeight: 1, letterSpacing: '-0.01em' }}>
          Running Title
        </Title>
      </Group>

      <Group gap="md" wrap="nowrap" style={{ flex: 1, justifyContent: 'center' }}>
        <ProjectSwitcher />
      </Group>

      <Group gap="xs" wrap="nowrap">
        <Tooltip label={computed === 'dark' ? 'Light mode' : 'Dark mode'}>
          <ActionIcon
            variant="subtle"
            size="lg"
            onClick={toggleColorScheme}
            aria-label="toggle theme"
          >
            {computed === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
          </ActionIcon>
        </Tooltip>

        <Menu position="bottom-end" withArrow>
          <Menu.Target>
            <Group gap="xs" style={{ cursor: 'pointer' }}>
              <Avatar radius="xl" size="sm" color="pine">
                {initials}
              </Avatar>
              <Text size="sm" visibleFrom="sm">
                {user?.username ?? '...'}
              </Text>
            </Group>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>{user?.email}</Menu.Label>
            <Menu.Item leftSection={<IconUser size={16} />} onClick={() => navigate('/profile')}>
              Profile (soon)
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              color="red"
              leftSection={<IconLogout size={16} />}
              onClick={async () => {
                await performLogout(queryClient);
                navigate('/login', { replace: true });
              }}
            >
              Log out
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Group>
  );
}
